import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getAlpinaLink } from '@/store/topics/actions';

import { selectTopics } from '@/store/topics/selectors';
import { selectUserDomainCompanyImages, selectUsers } from '@/store/users/selectors';

import { Button, Flex, Image, Skeleton, Tag, Tooltip, Typography } from 'antd';

import { MaterialCost } from '../MaterialCard/MaterialCost';
import { SchedulePopover } from '../schedule-popover';

import useImage from '@shared/hooks/useImage';
import useIsMobile from '@shared/hooks/useIsMobile';
import userStorage from '@shared/services/user-storage';
import { formatFullDate } from '@shared/utils/date-utils';
import { getMaterialIcon, isCorpCourseWithOrWithoutApprove } from '@shared/utils/topic-utils';

import Utils from '@/Utils';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import noun from 'plural-ru';

import css from './PlanningCard.module.scss';

const { Title, Text } = Typography;
const MAX_LINK_LENGTH = 25;

const PlanningCard = ({ material, children, loading, additionalClassName, isPlanning = false }) => {
  const { topic, event, startDate } = material;

  const [correctDate, setCorrectDate] = useState();
  const [smartCorrectDate, setSmartCorrectDate] = useState();

  const { t } = useTranslation('materialCard');
  const [isMobile] = useIsMobile(1200);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentUser } = useSelector(selectUsers);
  const { isLoadingLink } = useSelector(selectTopics);
  const { corpCourseIcon } = useSelector(selectUserDomainCompanyImages);

  const isBlocked = topic.scope !== 'EXTERNAL' && userStorage.getItem('externalLogin');

  // Отдаёт UUID фона для корп. курса, если он есть
  const thumbnailUuid = isCorpCourseWithOrWithoutApprove(topic?.type?.id)
    ? topic?.thumbFileUuid || currentUser?.domainCompany?.corpCourseBackground
    : topic?.thumbFileUuid;

  const image = useImage(thumbnailUuid, 'planning', isBlocked ? '404' : topic?.type?.id);

  // Для добавления ещё тэга
  const TAGS = [
    {
      name: 'inTrack',
      condition: !isEmpty(material.trackInfo),
      tooltip: (
        <>
          {t('usedInTrack', { name: material?.trackInfo?.[0]?.trackName })}
          {material.trackInfo.length > 1 &&
            noun(
              material.trackInfo.length - 1,
              t('usedInTrackMore.0', { count: material?.trackInfo?.length - 1 }),
              t('usedInTrackMore.1', { count: material?.trackInfo?.length - 1 }),
              t('usedInTrackMore.2', { count: material?.trackInfo?.length - 1 })
            )}
        </>
      ),
      title: t('track'),
    },
    {
      name: 'otherPasseges',
      condition: !!material?.multiStudyPlan,
      tooltip: t('otherPassegesTitle'),
      title: t('otherPasseges'),
      color: 'red',
    },
    {
      name: 'necessarily',
      condition: material?.addedByAdmin,
      className: css['PlanningCard-tag-necessarily'],
      title: t('necessarily'),
    },
    {
      name: 'optional',
      condition: !material?.addedByAdmin,
      className: css['PlanningCard-tag-optional'],
      title: t('optional'),
    },
    {
      name: 'inIpr',
      condition: material?.inIpr,
      title: t('careerPlan'),
      color: 'green',
    },
  ];

  const tagsRender = useMemo(() => {
    const filteredTags = TAGS.filter(tag => tag?.condition);

    return filteredTags.map(tag => (
      <Tooltip key={tag?.name} title={tag?.tooltip}>
        <Tag
          className={classNames(css['PlanningCard-tag'], {
            [tag?.className]: !!tag?.className,
            [css['PlanningCard-tag-default']]: !tag?.color,
          })}
          color={tag?.color}
        >
          {tag?.title}
        </Tag>
      </Tooltip>
    ));
  }, [material, TAGS]);

  const materialIcon = useMemo(() => {
    const icon = getMaterialIcon(topic?.type?.id);

    if (isCorpCourseWithOrWithoutApprove(topic?.type?.id) && corpCourseIcon) {
      return <img src={corpCourseIcon} alt='' />;
    } else if (typeof icon === 'function') {
      return icon();
    } else {
      return <img src={icon} alt='' />;
    }
  }, [corpCourseIcon, topic?.type?.id]);

  // Оставил старую логику даты
  useEffect(() => {
    if (topic.scope !== 'EXTERNAL' && userStorage.getItem('externalLogin')) {
      return;
    }

    if (currentUser) {
      let fullDate = '';
      const hoursOffset = currentUser.timezone.offset / 60 - currentUser.domainCompany.timezone.offset / 60;

      if (topic?.dates?.length > 0) {
        fullDate = `${topic.dates[0].startDate}T${topic.dates[0].startTime}`;
        setCorrectDate(dayjs(fullDate).add(hoursOffset, 'h'));
      } else if (event !== null) {
        fullDate = `${event.periods[0].date}T${event.periods[0].startTime}`;
        setCorrectDate(dayjs(fullDate).add(hoursOffset, 'h'));
      } else if (topic.startDate !== null && topic.startTime !== null) {
        fullDate = `${topic.startDate}T${topic.startTime}`;
        setCorrectDate(dayjs(fullDate));
      } else if (startDate !== null) {
        fullDate = `${startDate}T00:00`;
        setCorrectDate('');
        setSmartCorrectDate(dayjs(fullDate));
      }
    } else {
      setCorrectDate('');
      setSmartCorrectDate('');
    }
  }, [currentUser, material, event, startDate]);

  // Корректное расписание событий
  const schedule = useMemo(() => {
    if (event && !!event?.periods) {
      return {
        topicTypeId: topic?.type?.id,
        comment: event.comment,
        periods: event.periods,
      };
    } else if (topic.dates && topic.dates.length) {
      return {
        topicTypeId: material?.topic?.type?.id,
        comment: topic.comment,
        periods: topic.dates.map(date => ({
          id: date.id,
          date: date.startDate,
          startTime: date.startTime,
          endTime: date.endTime,
          place: date.place,
          eventLink: date.eventLink,
        })),
      };
    }
  }, [event, topic.comment, topic.dates]);

  // Рендер даты
  const dateRender = useMemo(() => {
    const correctDateRender = `${formatFullDate(correctDate)} ${t('at')} ${dayjs(correctDate).format('HH:mm')}`;
    const smartCorrectDateRender = `${formatFullDate(smartCorrectDate)}`;
    if (material.finishedDate) {
      return (
        <>
          {formatFullDate(material.finishedDate)}
          {schedule && <SchedulePopover schedule={schedule} user={currentUser} />}
        </>
      );
    }

    return (
      <>
        {correctDate ? correctDateRender : smartCorrectDateRender}
        {/* Расписание */}
        {schedule && <SchedulePopover schedule={schedule} user={currentUser} />}
      </>
    );
  }, [correctDate, smartCorrectDate, currentUser, schedule]);

  const linkRedirect = link => {
    if (link.undeletable) {
      dispatch(getAlpinaLink(link.url, res => res.url && window.open(res.url, '_blank')));
    } else {
      window.open(link.url, '_blank');
    }
  };

  // Ссылки
  const renderLinks = useMemo(() => {
    if (!Array.isArray(topic?.links) || topic?.links.length === 0) {
      return <></>;
    }

    const alpinaLink = topic?.links.find(link => link.name.includes('alpina') && !link.name.includes('alpinabook'));

    const targetLink = alpinaLink || topic?.links[0];

    return (
      <Button
        className={css['PlanningCard-link']}
        onClick={() => linkRedirect(targetLink)}
        type='link'
        loading={isLoadingLink}
      >
        {Utils.trString(targetLink.name, MAX_LINK_LENGTH)}
      </Button>
    );
  }, [topic?.links]);

  const onClickPlanning = () => material?.topic?.id && navigate(`/collection-topics/${material?.topic?.id}`);

  const renderAdditional = (
    <div
      className={classNames(css['PlanningCard-additional'], additionalClassName, {
        [css['PlanningCard-additional-planning']]: isPlanning,
      })}
    >
      {children}
    </div>
  );

  const checkRenderAdditional = (isPlanning && !isMobile) || !isPlanning;

  return (
    <Tooltip title={isBlocked && t('blocked')} placement='top'>
      <div
        className={classNames(
          css['PlanningCard'],
          { [css['PlanningCard-blocked']]: isBlocked },
          { [css['PlanningCard-planning']]: isPlanning }
          // { [css['PlanningCard-planningFinished']]: isPlanningFinished }
        )}
        data-qa='planningCard'
      >
        <div onClick={onClickPlanning} className={css['PlanningCard-image-container']}>
          {loading ? (
            <Skeleton.Image active className={css['PlanningCard-image-skeleton']} />
          ) : (
            <>
              <Image
                onClick={onClickPlanning}
                preview={false}
                className={css['PlanningCard-image']}
                src={image}
                height='100%'
                width='100%'
                style={{
                  background: topic.coverColor,
                }}
              />
              {/* Цена */}
              {!!topic?.cost && (
                <span className={css['PlanningCard-image-cost']}>
                  <MaterialCost topic={topic} currentUser={currentUser} />
                </span>
              )}
              {/* Иконка */}
              <span className={css['PlanningCard-image-icon']}>{materialIcon}</span>
            </>
          )}
        </div>
        <Skeleton className={css['PlanningCard-content-skeleton']} loading={loading} active>
          <Flex
            className={classNames(css['PlanningCard-content'], { [css['PlanningCard-content-planning']]: isPlanning })}
            vertical
          >
            {/* Content */}
            <Flex vertical gap={6}>
              {/* Тэги */}
              <Flex gap={6} wrap>
                {tagsRender}
              </Flex>

              {/* Заголовок */}
              <Title
                className={css['PlanningCard-content-title']}
                level={5}
                onClick={onClickPlanning}
                ellipsis={{
                  rows: 1,
                  tooltip: topic?.name,
                }}
              >
                {topic?.name}
              </Title>

              {/* Место встречи */}
              {topic?.meetPlace && (
                <Text
                  ellipsis={{
                    rows: 1,
                    tooltip: topic?.meetPlace,
                  }}
                >
                  {topic?.meetPlace}
                </Text>
              )}

              {/* Ссылки */}
              <div>{renderLinks}</div>

              {/* Дата мероприятий */}
              <Flex vertical={isMobile} align={isMobile && 'flex-start'}>
                {dateRender}
              </Flex>
            </Flex>
            {isPlanning && isMobile && renderAdditional}
          </Flex>
          {checkRenderAdditional && renderAdditional}
          {/* Additional */}
        </Skeleton>
      </div>
    </Tooltip>
  );
};

export default PlanningCard;
